import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  AssignStakeholdersDto,
  UserProfileDto,
  UserRole,
} from "@green0meter/api-client";
import { AxiosError } from "axios";

import { roleGuard } from "@api";
import { doubleMaterialityQuestionsApi } from "@api/services";

const unassignStakeholders = async (params: {
  questionId: string;
  payload: AssignStakeholdersDto;
}): Promise<void> => {
  const { questionId, payload } = params;
  const response =
    await doubleMaterialityQuestionsApi.doubleMaterialityQuestionsControllerUnassignStakeholder(
      questionId,
      payload,
    );

  return response.data;
};

export const useUnassignStakeholders = (
  user: UserProfileDto | null,
): {
  unassignStakeholders: (params: {
    questionId: string;
    payload: AssignStakeholdersDto;
  }) => void;
  unassignStakeholdersAsync: (params: {
    questionId: string;
    payload: AssignStakeholdersDto;
  }) => Promise<void>;
  unassignStakeholdersEnabled: boolean;
  isPendingUnassignStakeholders: boolean;
} => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const enabled = roleGuard(user, [UserRole.Owner, UserRole.Manager]);
  const { mutate, mutateAsync, isPending } = useMutation({
    mutationFn: enabled ? unassignStakeholders : undefined,
    onError: (error: AxiosError) => {
      const message =
        (error.response?.data as { message?: string })?.message || error.message;

      enqueueSnackbar(
        message ||
          t("common.errors.generic", "An error occurred. Please try again."),
        { variant: "error" },
      );
    },
  });

  return {
    unassignStakeholders: mutate,
    unassignStakeholdersAsync: mutateAsync,
    unassignStakeholdersEnabled: enabled,
    isPendingUnassignStakeholders: isPending,
  };
};
